import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPlus, Mail, User, Lock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import api from '@/lib/api';

export default function InviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [error, setError] = useState(null);
  
  // Registration form
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    loadInviteInfo();
  }, [token]);

  const loadInviteInfo = async () => {
    try {
      const res = await api.get(`/integrator/invite/${token}`);
      setInviteInfo(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invito non valido o scaduto');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!inviteInfo.is_existing_user) {
      if (!name.trim()) {
        toast.error('Inserisci il tuo nome');
        return;
      }
      if (!password || password.length < 6) {
        toast.error('La password deve avere almeno 6 caratteri');
        return;
      }
      if (password !== confirmPassword) {
        toast.error('Le password non corrispondono');
        return;
      }
    }
    
    setAccepting(true);
    try {
      await api.post(`/integrator/invite/${token}/accept`, {
        token,
        name: name || undefined,
        password: password || undefined,
      });
      
      toast.success('Invito accettato!');
      
      // Redirect based on user type
      if (inviteInfo.is_existing_user) {
        navigate('/dashboard');
      } else {
        navigate('/login');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'accettazione');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="text-xl font-bold mb-2">Invito Non Valido</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button asChild>
              <Link to="/login">Vai al Login</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
            <UserPlus className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Invito da {inviteInfo.integrator_name}</CardTitle>
          <CardDescription>
            {inviteInfo.is_existing_user 
              ? 'Accetta l\'invito per unirti al team'
              : 'Crea il tuo account per unirti al team'}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Email display */}
          <div className="p-4 rounded-lg bg-secondary/50">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="font-medium">{inviteInfo.email}</p>
              </div>
            </div>
          </div>

          {/* Registration form for new users */}
          {!inviteInfo.is_existing_user && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    placeholder="Il tuo nome"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10"
                    data-testid="invite-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Almeno 6 caratteri"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10"
                    data-testid="invite-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Conferma Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Ripeti la password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    data-testid="invite-confirm-password-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Accept button */}
          <Button 
            className="w-full" 
            size="lg"
            onClick={handleAccept}
            disabled={accepting}
            data-testid="accept-invite-btn"
          >
            {accepting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Accettazione...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                {inviteInfo.is_existing_user ? 'Accetta Invito' : 'Crea Account e Accetta'}
              </>
            )}
          </Button>

          {/* Login link for existing users */}
          {inviteInfo.is_existing_user && (
            <p className="text-center text-sm text-muted-foreground">
              Devi essere loggato per accettare l'invito.{' '}
              <Link to="/login" className="text-primary hover:underline">
                Accedi
              </Link>
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
